import type { GradeSlice, RevealStages } from '@kanjido/database'

/**
 * Slices the stroke paths into the reveal strip's cells.
 *
 * @remarks
 * Shared by Learn's `<StrokeAffordance />` and Atlas's
 * `<ProgressiveStrokes />` (canon spec §1.1): `null` renders cumulative
 * stroke-by-stroke cells; `false` renders nothing (callers gate the
 * section); a cuts array renders cumulative checkpoint cells, with the
 * complete cell appended unless the last cut already equals the stroke
 * count. An empty cuts array is not an authored value — the DB stores
 * null for "no curation" — but it degrades safely to a single complete
 * cell, which is what the null branch's last cell would be anyway.
 */
export function getStageSlices(
    strokePaths: string[],
    reveal: RevealStages | null
): string[][] {
    if (reveal === false) return []
    const cuts = reveal ?? strokePaths.map((_, i) => i + 1)
    const slices = cuts.map((c) => strokePaths.slice(0, c))
    const last = slices[slices.length - 1]
    if (last?.length !== strokePaths.length) slices.push(strokePaths)
    return slices
}

/**
 * Slices the stroke paths into one strip cell per grade-slice range.
 *
 * @remarks
 * Slice-class rendering (canon spec §1.2): delta cells only, never a
 * complete-character cell — a slice never renders its full host. Ranges
 * are clamped to the fetched svg's stroke sequence exactly as
 * {@link getGradeIndices} clamps them, so display and grading agree on
 * what a stale or over-reaching curation resolves to; a range that
 * survives clamping inverted (end before start) contributes no cell. A
 * null or empty `grade_slice` never reaches here: uncurated slice-class
 * rows fall back to the text glyph, and unflagged rows have no slice to
 * render.
 */
export function getSliceCells(
    strokePaths: string[],
    slice: GradeSlice
): string[][] {
    const cells: string[][] = []
    for (const [start, end] of slice) {
        const from = Math.max(1, start)
        const to = Math.min(strokePaths.length, end)
        if (to < from) continue
        cells.push(strokePaths.slice(from - 1, to))
    }
    return cells
}
